import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, BadgeCheck, Clock, Users } from 'lucide-react';
import { pricingPackages, additionalPricing } from '../data/mock';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Transparante Tarieven</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Geen verborgen kosten. Kies het pakket dat bij jou past en start vandaag nog met je rijlessen.
          </p>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPackages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative border-none shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  pkg.popular ? 'ring-2 ring-blue-600 transform scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center">
                      <BadgeCheck className="w-4 h-4 mr-1" />
                      Populairste Keuze
                    </span>
                  </div>
                )}
                <CardContent className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="mb-4">
                    <div className="text-4xl font-bold text-blue-600 mb-1">{pkg.price}</div>
                    <div className="text-sm text-gray-600">{pkg.duration}</div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <div key={i} className="flex items-center space-x-2 text-left">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button className={`w-full ${
                      pkg.popular 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}>
                      Kies dit pakket
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">* Alle prijzen zijn exclusief examenkosten</p>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Overige Tarieven</h2>
            <div className="space-y-4">
              {additionalPricing.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="text-lg font-medium text-gray-900">{item.service}</span>
                  <span className="text-xl font-bold text-blue-600">{item.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Betaalmogelijkheden</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-none shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Direct betalen</h3>
                  <p className="text-gray-600">
                    Betaal je pakket in één keer en profiteer van de beste prijs.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Gespreid betalen</h3>
                  <p className="text-gray-600">
                    Betaal je pakket in termijnen. Vraag naar de mogelijkheden.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Per les betalen</h3>
                  <p className="text-gray-600">
                    Liever per les betalen? Ook dat is mogelijk bij ons.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Veelgestelde vragen over tarieven</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Wat is inbegrepen in de pakketten?</h3>
                <p className="text-gray-600">
                  Alle lespakketten bevatten rijlessen van 60 minuten met een vaste instructeur, theorie ondersteuning en persoonlijke begeleiding. Examenkosten zijn niet inbegrepen.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Welk pakket past bij mij?</h3>
                <p className="text-gray-600">
                  Voor beginners zonder ervaring raden we het 30 of 40 lessen pakket aan. Heb je al rijervaring? Dan kan 20 lessen voldoende zijn. Tijdens je proefles adviseren we je graag.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Kan ik later lessen bijkopen?</h3>
                <p className="text-gray-600">
                  Ja, je kunt altijd losse lessen bijkopen voor €63 per les. Je kunt ook upgraden naar een groter pakket.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Klaar om te beginnen?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Plan je proefles met korting en ontvang persoonlijk advies over het beste pakket voor jou.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              Plan Proefles Met 30% Korting
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;